import { InfoOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Divider,
  TextField,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  Switch,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import TimeWarranty from "./TimeWarranty.component";
import WarrantyInfoPopup from "./WarrantyInfoPopup.component";

const canEnableToggle = (row) => {
  const hasLabel = Boolean(
    row.warranty_type_en?.trim?.() || row.warranty_type_es?.trim?.()
  );
  const hasMonths = row.coverage_months !== "" && row.coverage_months != null;
  const hasMiles = row.coverage_miles !== "" && row.coverage_miles != null;
  return hasLabel && hasMonths && hasMiles;
};

const GasRow = ({ row, onChange, infoLabel, showInfo }) => {
  const [open, setOpen] = useState(false);
  const isEnabled = row.is_enabled !== false;
  const canEnable = canEnableToggle(row);
  const toggleDisabled = !isEnabled && !canEnable;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {showInfo && (

            <span sx={{ width: 40, flexShrink: 0 }}>
              <Switch
                size="small"
                checked={isEnabled}
                disabled={toggleDisabled}
                onChange={(e) =>
                  onChange(row.id, "is_enabled", e.target.checked)
                }
              />
            </span>
         
        )}
        <TextField
          size="medium"
          label="English"
          value={row.warranty_type_en}
          onChange={(e) => onChange(row.id, "warranty_type_en", e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TextField
          size="medium"
          label="Spanish"
          value={row.warranty_type_es}
          onChange={(e) => onChange(row.id, "warranty_type_es", e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TextField
          size="medium"
          label="Months"
          type="number"
          value={row.coverage_months}
          onChange={(e) => onChange(row.id, "coverage_months", e.target.value)}
          onWheel={(e) => e.target.blur()}
          inputProps={{ min: 0 }}
          sx={{ width: 140, flexShrink: 0 }}
        />
        <TextField
          size="medium"
          label="Miles"
          type="number"
          value={row.coverage_miles}
          onChange={(e) => onChange(row.id, "coverage_miles", e.target.value)}
          onWheel={(e) => e.target.blur()}
          inputProps={{ min: 0 }}
          sx={{ width: 140, flexShrink: 0 }}
        />
        {showInfo && (
          <IconButton
            size="small"
            sx={{ color: "text.secondary", flexShrink: 0 }}
            onClick={() => infoLabel && setOpen(true)}
          >
            <InfoOutlined fontSize="small" />
          </IconButton>
        )}
      </Box>

      {showInfo && infoLabel && open ? (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            {infoLabel}
            <IconButton
              onClick={() => setOpen(false)}
              size="small"
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <WarrantyInfoPopup
            initialItems={row.infoItems}
            initialMeta={{
              info_title_en: row.info_title_en,
              info_title_es: row.info_title_es,
              info_subtitle_en: row.info_subtitle_en,
              info_subtitle_es: row.info_subtitle_es,
              info_footer_en: row.info_footer_en,
              info_footer_es: row.info_footer_es,
            }}
            onClose={() => setOpen(false)}
            onSave={(fields) => onChange(row.id, fields)}
          />
        </Dialog>
      ) : null}
    </>
  );
};

const GasSubSection = ({ label, rows, onChange, showInfo }) => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
        px: 0.5,
      }}
    >
      {/* {showInfo && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ width: 40, textAlign: "center", flexShrink: 0 }}
        >
          Show
        </Typography>
      )} */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ width: 80, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Box sx={{ flex: 1 }} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 140, textAlign: "center", flexShrink: 0 }}
      >
        Number of Months
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 140, textAlign: "center", flexShrink: 0 }}
      >
        Number of Miles
      </Typography>
      {showInfo && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ width: 40, textAlign: "center", flexShrink: 0 }}
        >
          Info
        </Typography>
      )}
    </Box>
    {rows?.map((row) => (
      <GasRow
        key={row.id}
        row={row}
        onChange={onChange}
        infoLabel={showInfo ? row.warranty_type_en : null}
        showInfo={showInfo}
      />
    ))}
  </Box>
);

const GasWarranty = ({ warranty, setWarranty }) => {
  const handleChange = (subKey, rowId, field, value) => {
    setWarranty((prev) => ({
      ...prev,
      gas: {
        ...prev.gas,
        [subKey]: prev.gas[subKey].map((row) => {
          if (row.id !== rowId) return row;
          // Patch object from I-popup (title / subtitle / footer / infoItems)
          if (field && typeof field === "object" && value === undefined) {
            return { ...row, ...field };
          }
          return { ...row, [field]: value };
        }),
      },
    }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: "12px 4px",
        width: "100%",
      }}
    >
      <GasSubSection
        label="Minor"
        rows={warranty?.minor}
        onChange={(id, field, val) => handleChange("minor", id, field, val)}
        showInfo={false}
      />
      <Divider />
      <GasSubSection
        label="Major"
        rows={warranty?.major}
        onChange={(id, field, val) => handleChange("major", id, field, val)}
        showInfo={true}
      />
      <Divider />
      <TimeWarranty
        time={warranty?.time}
        setWarranty={setWarranty}
        sectionKey="gas"
      />
    </Box>
  );
};

export default GasWarranty;